import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LabelList
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import './ERDashboard.css';
import './TCOCDashboard.css';
import { BIGQUERY_CONFIG } from '../config';

import { API_BASE_URL } from '../config';
const PROJECT_ID = BIGQUERY_CONFIG.PROJECT;
const DATASET_NAME = BIGQUERY_CONFIG.DATASET;

const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const formatNumber = (value) => Number(value || 0).toLocaleString('en-US');
const formatRiskScore = (value) => (Number(value || 0)).toFixed(3);
const formatPercent = (value) => `${(Number(value || 0)).toFixed(1)}%`;

function TCOCDeepDive({ benchmarkData }) {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    total_benes: 0,
    total_person_years: 0,
    avg_risk_score: 0,
    total_cost: 0,
    esrd_benes: 0,
    esrd_person_years: 0,
    esrd_risk_score: 0,
    esrd_total_cost: 0,
    dis_benes: 0,
    dis_person_years: 0,
    dis_risk_score: 0,
    dis_total_cost: 0,
    ad_benes: 0,
    ad_person_years: 0,
    ad_risk_score: 0,
    ad_total_cost: 0,
    nd_benes: 0,
    nd_person_years: 0,
    nd_risk_score: 0,
    nd_total_cost: 0
  });

  const [data, setData] = useState({
    monthlyCost: [],
    snfUserData: [],
    hhaUserData: [],
    hosUserData: [],
    erUserData: [],
    inpUserData: [],
    chronicConditions: [],
    riskGrouping: [],
    ageGrouping: [],
    cohortData: []
  });

  const [viewMode, setViewMode] = useState('benes'); // benes, total_cost, avg_risk, pmpy, pmpy_risk_adjusted, mlr
  const [chartMode, setChartMode] = useState('total_cost'); // 'total_cost' | 'pmpy'
  const [chartData, setChartData] = useState({
    snfUserData: [],
    hhaUserData: [],
    hosUserData: [],
    erUserData: [],
    inpUserData: [],
    chronicConditions: [],
    riskGrouping: [],
    ageGrouping: [],
    cohortData: []
  });

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateChartValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, data, benchmarkData]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      console.log('Fetching TCOC Deep Dive data...');
      const response = await axios.get(`${API_BASE_URL}/api/tcoc/deepdive`, {
        params: {
          project: PROJECT_ID,
          dataset: DATASET_NAME
        }
      });

      console.log('TCOC Deep Dive response:', response.data);
      
      if (response.data) {
        setMetrics(response.data.metrics || {});
        setData(response.data.data || {});
      }
    } catch (error) {
      console.error('Error fetching TCOC deep dive data:', error);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateMLR = (item) => {
    if (!benchmarkData || !metrics) return 0;
    
    const totalCost = item.total_cost || 0;
    const personYears = item.total_benes ? (totalCost / (item.pmpy || 1)) / 12 : 0; // Approximate person years from total cost and PMPY
    
    if (totalCost === 0 || personYears === 0) return 0;
    
    // Calculate weighted benchmark cost using cohort person years from metrics
    const cohorts = ['esrd', 'dis', 'ad', 'nd'];
    let totalBenchmarkCost = 0;
    
    cohorts.forEach(cohortKey => {
      const cohortPersonYears = metrics[`${cohortKey}_person_years`] || 0;
      const pyRisk = metrics[`${cohortKey}_risk_score`] || 1;
      const benchmark = benchmarkData?.benchmarks?.[cohortKey] || 0;
      const trend = benchmarkData?.trends?.[cohortKey] || 1;
      const by3Risk = benchmarkData?.riskScores?.[cohortKey] || 1;
      
      if (benchmark > 0 && by3Risk > 0 && cohortPersonYears > 0) {
        // Updated benchmark PMPY = benchmark * (PY Risk / BY3 Risk) * Trend
        const updatedBenchmarkPMPY = benchmark * (pyRisk / by3Risk) * trend;
        // Total benchmark cost for this cohort
        const cohortBenchmarkCost = updatedBenchmarkPMPY * cohortPersonYears;
        totalBenchmarkCost += cohortBenchmarkCost;
      }
    });
    
    // MLR = (PY Total Cost / Benchmark Total Cost) * 100
    const mlr = totalBenchmarkCost > 0 ? (totalCost / totalBenchmarkCost) * 100 : 0;
    
    return mlr;
  };

  const updateChartValues = () => {
    if (!data) return;
    
    const updateDataArray = (dataArray) => {
      return dataArray.map(item => {
        let value;
        switch (viewMode) {
          case 'benes':
            value = item.total_benes || 0;
            break;
          case 'total_cost':
            value = item.total_cost || 0;
            break;
          case 'avg_risk':
            value = item.avg_risk_score || 0;
            break;
          case 'pmpy':
            value = item.pmpy || 0;
            break;
          case 'pmpy_risk_adjusted':
            value = item.pmpy_risk_adjusted || 0;
            break;
          case 'mlr':
            value = calculateMLR(item);
            break;
          default:
            value = item.total_benes || 0;
        }
        return { ...item, value };
      });
    };

    setChartData({
      snfUserData: updateDataArray(data.snfUserData || []),
      hhaUserData: updateDataArray(data.hhaUserData || []),
      hosUserData: updateDataArray(data.hosUserData || []),
      erUserData: updateDataArray(data.erUserData || []),
      inpUserData: updateDataArray(data.inpUserData || []),
      chronicConditions: updateDataArray(data.chronicConditions || []),
      riskGrouping: updateDataArray(data.riskGrouping || []),
      ageGrouping: updateDataArray(data.ageGrouping || []),
      cohortData: updateDataArray(data.cohortData || [])
    });
  };

  const getDisplayLabel = () => {
    switch (viewMode) {
      case 'benes':
        return 'Beneficiaries';
      case 'total_cost':
        return 'Total Cost';
      case 'avg_risk':
        return 'Avg Risk Score';
      case 'pmpy':
        return 'PMPY';
      case 'pmpy_risk_adjusted':
        return 'PMPY Risk Adjusted';
      case 'mlr':
        return 'MLR %';
      default:
        return '';
    }
  };

  const formatDisplayValue = (value) => {
    switch (viewMode) {
      case 'benes':
        return formatNumber(value);
      case 'total_cost':
      case 'pmpy':
      case 'pmpy_risk_adjusted':
        return formatCurrency(value);
      case 'avg_risk':
        return formatRiskScore(value);
      case 'mlr':
        return formatPercent(value);
      default:
        return value;
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', padding: '10px', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
          <p style={{margin: 0, fontSize: '12px', fontWeight: 'bold'}}>{payload[0].payload.name || payload[0].payload.category}</p>
          <p style={{margin: '5px 0 0 0', fontSize: '12px'}}>{formatDisplayValue(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div style={{ width: '100%', padding: '20px', backgroundColor: '#f3f4f6', minHeight: '100vh' }}>
        <div style={{ animation: 'fadeIn 0.5s ease-in-out' }}>
          {/* Pulse Skeleton for Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '20px' }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} style={{
                backgroundColor: '#e5e7eb',
                borderRadius: '8px',
                padding: '20px',
                height: '120px',
                animation: `pulse 1.5s ease-in-out infinite`,
                animationDelay: `${i * 0.1}s`
              }} />
            ))}
          </div>
          {/* Pulse Skeleton for Charts */}
          <div style={{
            backgroundColor: '#e5e7eb',
            borderRadius: '8px',
            padding: '20px',
            height: '400px',
            marginBottom: '20px',
            animation: 'pulse 1.5s ease-in-out infinite',
            animationDelay: '0.5s'
          }} />
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', margin: 0, padding: '20px', backgroundColor: '#f3f4f6', minHeight: '100vh', animation: 'fadeIn 0.5s ease-in-out' }}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      
      {/* Top Statistics */}
      <div className="metrics-row" style={{ padding: '0', marginBottom: '20px' }}>
        <div className="metric-card">
          <div className="metric-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
            👥 TOTAL BENES
          </div>
          <div className="metric-value">{formatNumber(metrics.total_benes)}</div>
          <div style={{ fontSize: '10px', marginTop: '8px', color: '#6b7280' }}>
            <div>ESRD: {formatNumber(metrics.esrd_benes)}</div>
            <div>DIS: {formatNumber(metrics.dis_benes)}</div>
            <div>AD: {formatNumber(metrics.ad_benes)}</div>
            <div>ND: {formatNumber(metrics.nd_benes)}</div>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
            📅 TOTAL PERSON YEARS
          </div>
          <div className="metric-value">{formatNumber(metrics.total_person_years)}</div>
          <div style={{ fontSize: '10px', marginTop: '8px', color: '#6b7280' }}>
            <div>ESRD: {formatNumber(metrics.esrd_person_years)}</div>
            <div>DIS: {formatNumber(metrics.dis_person_years)}</div>
            <div>AD: {formatNumber(metrics.ad_person_years)}</div>
            <div>ND: {formatNumber(metrics.nd_person_years)}</div>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
            📊 AVG RISK SCORE
          </div>
          <div className="metric-value">{formatRiskScore(metrics.avg_risk_score)}</div>
          <div style={{ fontSize: '10px', marginTop: '8px', color: '#6b7280' }}>
            <div>ESRD: {formatRiskScore(metrics.esrd_risk_score)}</div>
            <div>DIS: {formatRiskScore(metrics.dis_risk_score)}</div>
            <div>AD: {formatRiskScore(metrics.ad_risk_score)}</div>
            <div>ND: {formatRiskScore(metrics.nd_risk_score)}</div>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
            💰 TOTAL COST
          </div>
          <div className="metric-value">{formatCurrency(metrics.total_cost)}</div>
          <div style={{ fontSize: '10px', marginTop: '8px', color: '#6b7280' }}>
            <div>ESRD: {formatCurrency(metrics.esrd_total_cost)}</div>
            <div>DIS: {formatCurrency(metrics.dis_total_cost)}</div>
            <div>AD: {formatCurrency(metrics.ad_total_cost)}</div>
            <div>ND: {formatCurrency(metrics.nd_total_cost)}</div>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
            📈 PMPY
          </div>
          <div className="metric-value">
            {formatCurrency(metrics.total_person_years > 0 ? metrics.total_cost / metrics.total_person_years : 0)}
          </div>
          <div style={{ fontSize: '10px', marginTop: '8px', color: '#6b7280' }}>
            <div>ESRD: {formatCurrency(metrics.esrd_person_years > 0 ? metrics.esrd_total_cost / metrics.esrd_person_years : 0)}</div>
            <div>DIS: {formatCurrency(metrics.dis_person_years > 0 ? metrics.dis_total_cost / metrics.dis_person_years : 0)}</div>
            <div>AD: {formatCurrency(metrics.ad_person_years > 0 ? metrics.ad_total_cost / metrics.ad_person_years : 0)}</div>
            <div>ND: {formatCurrency(metrics.nd_person_years > 0 ? metrics.nd_total_cost / metrics.nd_person_years : 0)}</div>
          </div>
        </div>
      </div>

      {/* Monthly Chart with Toggle */}
      <div style={{
        background: 'linear-gradient(135deg, #c8e6c9 0%, #e8f5e9 100%)',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ color: '#1b5e20', fontSize: '18px', fontWeight: '600', margin: 0 }}>
            Monthly {chartMode === 'total_cost' ? 'Total Cost' : 'PMPY'} by Cohort
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setChartMode('total_cost')}
              style={{
                padding: '7px 16px',
                backgroundColor: chartMode === 'total_cost' ? '#2e7d32' : '#a5d6a7',
                color: chartMode === 'total_cost' ? 'white' : '#2e7d32',
                border: 'none', borderRadius: '6px', cursor: 'pointer',
                fontSize: '13px', fontWeight: '600', transition: 'all 0.2s ease'
              }}
            >
              Total Cost
            </button>
            <button
              onClick={() => setChartMode('pmpy')}
              style={{
                padding: '7px 16px',
                backgroundColor: chartMode === 'pmpy' ? '#2e7d32' : '#a5d6a7',
                color: chartMode === 'pmpy' ? 'white' : '#2e7d32',
                border: 'none', borderRadius: '6px', cursor: 'pointer',
                fontSize: '13px', fontWeight: '600', transition: 'all 0.2s ease'
              }}
            >
              PMPY
            </button>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={data.monthlyCost}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis tickFormatter={formatCurrency} />
            <Tooltip
              cursor={{ fill: '#e0f2fe' }}
              formatter={(value) => formatCurrency(value)}
              contentStyle={{backgroundColor: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px'}}
            />
            {chartMode === 'total_cost' ? (
              <>
                <Line type="monotone" dataKey="ESRD" stroke="#8b0000" strokeWidth={2} />
                <Line type="monotone" dataKey="DIS" stroke="#ff6347" strokeWidth={2} />
                <Line type="monotone" dataKey="AD" stroke="#4169e1" strokeWidth={2} />
                <Line type="monotone" dataKey="ND" stroke="#32cd32" strokeWidth={2} />
                <Line type="monotone" dataKey="TOTAL" stroke="#000000" strokeWidth={3} />
              </>
            ) : (
              <>
                <Line type="monotone" dataKey="ESRD_PMPY" stroke="#8b0000" strokeWidth={2} name="ESRD" />
                <Line type="monotone" dataKey="DIS_PMPY" stroke="#ff6347" strokeWidth={2} name="DIS" />
                <Line type="monotone" dataKey="AD_PMPY" stroke="#4169e1" strokeWidth={2} name="AD" />
                <Line type="monotone" dataKey="ND_PMPY" stroke="#32cd32" strokeWidth={2} name="ND" />
                <Line type="monotone" dataKey="TOTAL_PMPY" stroke="#000000" strokeWidth={3} name="TOTAL" />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default TCOCDeepDive;
